import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MessageCircle, ShoppingCart, Phone, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Toaster, toast } from 'sonner';

// Pricing constants
const UNIT_PRICE = 3500; // 3,500 F CFA for both fresh and frozen
const MIN_QUANTITY = 2;

// Delivery zones with fees
const DELIVERY_ZONES = [
  { id: 'thies-city', label: 'Thiès Ville', fee: 1500 },
  { id: 'thies-region', label: 'Région de Thiès', fee: 3500 },
  { id: '55-100km', label: '55 à 100 km', fee: 5000 },
  { id: 'above-100km', label: 'Plus de 100 km', fee: null },
];

// Confirmation methods
const CONFIRMATION_METHODS = [
  { id: 'phone', label: 'Appel', icon: '📞' },
  { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { id: 'sms', label: 'SMS', icon: '📱' },
];

const QuickOrderPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    clientType: '',
    product: '',
    quantity: '2',
    deliveryMode: '',
    deliveryZone: '',
    deliveryDate: '',
    confirmationMethod: '',
    city: '',
    comment: ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [quantityError, setQuantityError] = useState('');

  // WhatsApp numbers
  const WHATSAPP_NUMBER_1 = '221781270846';
  const WHATSAPP_NUMBER_2 = '221779093914';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'quantity') {
      const qty = parseInt(value, 10);
      if (isNaN(qty) || qty < MIN_QUANTITY) {
        setQuantityError(`Commande minimum : ${MIN_QUANTITY} poulets.`);
      } else {
        setQuantityError('');
      }
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getProductName = (productId) => {
    const products = {
      'poulet-frais': 'Poulet Frais',
      'poulet-congele': 'Poulet Congelé'
    };
    return products[productId] || productId;
  };

  const getClientTypeName = (type) => {
    const types = {
      'particulier': 'Particulier',
      'restaurant': 'Restaurant',
      'revendeur': 'Revendeur'
    };
    return types[type] || type;
  };

  const getDeliveryModeName = (mode) => {
    const modes = {
      'livraison': 'Livraison',
      'retrait': 'Retrait sur place'
    };
    return modes[mode] || mode;
  };

  const getDeliveryZoneName = (zoneId) => {
    const zone = DELIVERY_ZONES.find(z => z.id === zoneId);
    return zone ? zone.label : zoneId;
  };

  const getConfirmationMethodName = (methodId) => {
    const method = CONFIRMATION_METHODS.find(m => m.id === methodId);
    return method ? method.label : methodId;
  };

  // Calculate prices
  const priceCalculation = useMemo(() => {
    const quantity = parseInt(formData.quantity, 10) || 0;
    const subtotal = quantity * UNIT_PRICE;
    
    let deliveryFee = 0;
    let deliveryFeeText = '';
    
    if (formData.deliveryMode === 'livraison' && formData.deliveryZone) {
      const zone = DELIVERY_ZONES.find(z => z.id === formData.deliveryZone);
      if (zone) {
        if (zone.fee === null) {
          deliveryFeeText = 'À confirmer';
        } else {
          deliveryFee = zone.fee;
        }
      }
    }
    
    return {
      subtotal,
      deliveryFee,
      deliveryFeeText,
      total: subtotal + deliveryFee,
      isAbove100km: formData.deliveryZone === 'above-100km'
    };
  }, [formData.quantity, formData.deliveryMode, formData.deliveryZone]);

  const generateWhatsAppMessage = () => {
    const { subtotal, deliveryFee, total, isAbove100km } = priceCalculation;
    
    let totalText = isAbove100km 
      ? `${subtotal.toLocaleString()} F CFA + livraison à confirmer`
      : `${total.toLocaleString()} F CFA`;
    
    const message = `🐔 *COMMANDE - Volaille Prime*

👤 *Client*
• Nom : ${formData.name}
• Tél : ${formData.phone}
• Type : ${getClientTypeName(formData.clientType)}
• Ville : ${formData.city}

📦 *Commande*
• Produit : ${getProductName(formData.product)}
• Quantité : ${formData.quantity} poulet(s)
• Mode : ${getDeliveryModeName(formData.deliveryMode)}
${formData.deliveryMode === 'livraison' ? `• Zone : ${getDeliveryZoneName(formData.deliveryZone)}` : ''}
• Date souhaitée : ${formData.deliveryDate}

📞 *Confirmation par* : ${getConfirmationMethodName(formData.confirmationMethod)}

💰 *Total* : ${totalText}

${formData.comment ? `📝 Note : ${formData.comment}` : ''}

_Merci de confirmer ma commande._`;

    return encodeURIComponent(message);
  };

  const validateForm = () => {
    const qty = parseInt(formData.quantity, 10);
    if (isNaN(qty) || qty < MIN_QUANTITY) {
      toast.error(`Commande minimum : ${MIN_QUANTITY} poulets.`);
      return false;
    }
    
    if (!formData.name || !formData.phone || !formData.clientType || 
        !formData.product || !formData.deliveryMode || !formData.city ||
        !formData.deliveryDate || !formData.confirmationMethod) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return false;
    }
    
    if (formData.deliveryMode === 'livraison' && !formData.deliveryZone) {
      toast.error('Veuillez sélectionner une zone de livraison');
      return false;
    }
    
    return true;
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const openWhatsApp = (number) => {
    const message = generateWhatsAppMessage();
    const url = `https://wa.me/${number}?text=${message}`;
    window.open(url, '_blank');
  };

  // Get today's date for min date
  const today = new Date().toISOString().split('T')[0];

  if (showPreview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50">
        <Toaster position="top-center" richColors />
        
        {/* Header */}
        <div className="bg-gradient-to-r from-burgundy to-burgundy-dark text-white py-4 px-4 text-center sticky top-0 z-10 shadow-lg">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle size={24} />
            <h1 className="text-xl font-bold">Récapitulatif</h1>
          </div>
        </div>

        <div className="container max-w-lg mx-auto px-4 py-6">
          {/* Order Summary Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-burgundy/5 p-4 border-b">
              <h2 className="font-bold text-burgundy text-lg">Votre Commande</h2>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Client Info */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Client</h3>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                  <p><span className="text-gray-500">Nom :</span> <span className="font-medium">{formData.name}</span></p>
                  <p><span className="text-gray-500">Tél :</span> <span className="font-medium">{formData.phone}</span></p>
                  <p><span className="text-gray-500">Type :</span> <span className="font-medium">{getClientTypeName(formData.clientType)}</span></p>
                  <p><span className="text-gray-500">Ville :</span> <span className="font-medium">{formData.city}</span></p>
                </div>
              </div>

              {/* Product Info */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Commande</h3>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                  <p><span className="text-gray-500">Produit :</span> <span className="font-medium">{getProductName(formData.product)}</span></p>
                  <p><span className="text-gray-500">Quantité :</span> <span className="font-medium">{formData.quantity} poulet(s)</span></p>
                  <p><span className="text-gray-500">Mode :</span> <span className="font-medium">{getDeliveryModeName(formData.deliveryMode)}</span></p>
                  {formData.deliveryMode === 'livraison' && (
                    <p><span className="text-gray-500">Zone :</span> <span className="font-medium">{getDeliveryZoneName(formData.deliveryZone)}</span></p>
                  )}
                  <p><span className="text-gray-500">Date souhaitée :</span> <span className="font-medium">{formData.deliveryDate}</span></p>
                  <p><span className="text-gray-500">Confirmation :</span> <span className="font-medium">{getConfirmationMethodName(formData.confirmationMethod)}</span></p>
                </div>
              </div>

              {/* Price Summary */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Total</h3>
                <div className="bg-gold/10 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sous-total</span>
                    <span className="font-medium">{priceCalculation.subtotal.toLocaleString()} F CFA</span>
                  </div>
                  {formData.deliveryMode === 'livraison' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Livraison</span>
                      <span className="font-medium">
                        {priceCalculation.deliveryFeeText || `${priceCalculation.deliveryFee.toLocaleString()} F CFA`}
                      </span>
                    </div>
                  )}
                  <div className="h-px bg-burgundy/20 my-2"></div>
                  <div className="flex justify-between text-lg">
                    <span className="font-bold text-burgundy">TOTAL</span>
                    <span className="font-bold text-burgundy">
                      {priceCalculation.isAbove100km 
                        ? `${priceCalculation.subtotal.toLocaleString()} F + livraison`
                        : `${priceCalculation.total.toLocaleString()} F CFA`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {formData.comment && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Note</h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-700 text-sm">{formData.comment}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* WhatsApp Buttons */}
          <div className="mt-6 space-y-3">
            <p className="text-center text-sm text-gray-600 font-medium">
              Envoyez votre commande via WhatsApp :
            </p>
            
            <Button
              onClick={() => openWhatsApp(WHATSAPP_NUMBER_1)}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
              data-testid="whatsapp-btn-1"
            >
              <MessageCircle className="mr-2" size={24} />
              78 127 08 46
            </Button>

            <Button
              onClick={() => openWhatsApp(WHATSAPP_NUMBER_2)}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 shadow-xl hover:shadow-2xl transition-all hover:scale-105"
              data-testid="whatsapp-btn-2"
            >
              <MessageCircle className="mr-2" size={24} />
              77 909 39 14
            </Button>
          </div>

          {/* Back Button */}
          <Button
            onClick={() => setShowPreview(false)}
            variant="outline"
            className="w-full mt-4 h-12 border-2"
            data-testid="back-btn"
          >
            ← Modifier la commande
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-burgundy to-burgundy-dark text-white py-4 px-4 text-center sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-center gap-2">
          <ShoppingCart size={24} />
          <h1 className="text-xl font-bold">Volaille Prime</h1>
        </div>
        <p className="text-xs text-gold mt-1">Commande Rapide via WhatsApp</p>
      </div>

      {/* Form */}
      <div className="container max-w-lg mx-auto px-4 py-6">
        <form onSubmit={handlePreview} className="space-y-5">
          
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-base font-semibold">
              Nom complet <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Ex: Amadou Diallo"
              value={formData.name}
              onChange={handleInputChange}
              className="text-base h-12 border-2 border-gray-300 focus:border-burgundy rounded-xl"
              data-testid="name-input"
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-base font-semibold">
              Téléphone <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="77 123 45 67"
              value={formData.phone}
              onChange={handleInputChange}
              className="text-base h-12 border-2 border-gray-300 focus:border-burgundy rounded-xl"
              data-testid="phone-input"
              required
            />
          </div>

          {/* Client Type */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Type de client <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'particulier', label: '👤 Particulier' },
                { id: 'restaurant', label: '🍽️ Restaurant' },
                { id: 'revendeur', label: '🏪 Revendeur' }
              ].map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleSelectChange('clientType', type.id)}
                  className={`h-14 rounded-xl border-2 font-medium text-sm transition-all ${
                    formData.clientType === type.id
                      ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                      : 'bg-white border-gray-300 hover:border-burgundy'
                  }`}
                  data-testid={`client-type-${type.id}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city" className="text-base font-semibold">
              Ville / Quartier <span className="text-red-500">*</span>
            </Label>
            <Input
              id="city"
              name="city"
              type="text"
              placeholder="Ex: Thiès Centre, Dakar..."
              value={formData.city}
              onChange={handleInputChange}
              className="text-base h-12 border-2 border-gray-300 focus:border-burgundy rounded-xl"
              data-testid="city-input"
              required
            />
          </div>

          {/* Product */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Produit <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSelectChange('product', 'poulet-frais')}
                className={`h-20 rounded-xl border-2 font-medium transition-all flex flex-col items-center justify-center ${
                  formData.product === 'poulet-frais'
                    ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                    : 'bg-white border-gray-300 hover:border-burgundy'
                }`}
                data-testid="product-fresh"
              >
                <span className="text-2xl mb-1">🐔</span>
                <span>Poulet Frais</span>
                <span className="text-xs opacity-80">3 500 F CFA</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectChange('product', 'poulet-congele')}
                className={`h-20 rounded-xl border-2 font-medium transition-all flex flex-col items-center justify-center ${
                  formData.product === 'poulet-congele'
                    ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                    : 'bg-white border-gray-300 hover:border-burgundy'
                }`}
                data-testid="product-frozen"
              >
                <span className="text-2xl mb-1">❄️</span>
                <span>Poulet Congelé</span>
                <span className="text-xs opacity-80">3 500 F CFA</span>
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity" className="text-base font-semibold">
              Quantité <span className="text-red-500">*</span> <span className="text-sm text-gray-500">(min. 2)</span>
            </Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="h-12 w-14 text-2xl border-2 rounded-xl"
                onClick={() => {
                  const newQty = Math.max(MIN_QUANTITY, parseInt(formData.quantity) - 1);
                  setFormData(prev => ({ ...prev, quantity: newQty.toString() }));
                  setQuantityError('');
                }}
                data-testid="qty-decrease"
              >
                -
              </Button>
              <Input
                id="quantity"
                name="quantity"
                type="number"
                min={MIN_QUANTITY}
                value={formData.quantity}
                onChange={handleInputChange}
                className={`text-center text-2xl h-12 border-2 font-bold rounded-xl flex-1 ${quantityError ? 'border-red-500' : 'border-gray-300'}`}
                data-testid="quantity-input"
                required
              />
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="h-12 w-14 text-2xl border-2 rounded-xl"
                onClick={() => {
                  const newQty = parseInt(formData.quantity) + 1;
                  setFormData(prev => ({ ...prev, quantity: newQty.toString() }));
                  setQuantityError('');
                }}
                data-testid="qty-increase"
              >
                +
              </Button>
            </div>
            {quantityError && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle size={14} />
                {quantityError}
              </p>
            )}
          </div>

          {/* Delivery Mode */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Mode de réception <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSelectChange('deliveryMode', 'livraison')}
                className={`h-16 rounded-xl border-2 font-semibold transition-all ${
                  formData.deliveryMode === 'livraison'
                    ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                    : 'bg-white border-gray-300 hover:border-burgundy'
                }`}
                data-testid="delivery-mode-livraison"
              >
                🚚 Livraison
              </button>
              <button
                type="button"
                onClick={() => {
                  handleSelectChange('deliveryMode', 'retrait');
                  handleSelectChange('deliveryZone', ''); // Clear zone when switching to pickup
                }}
                className={`h-16 rounded-xl border-2 font-semibold transition-all ${
                  formData.deliveryMode === 'retrait'
                    ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                    : 'bg-white border-gray-300 hover:border-burgundy'
                }`}
                data-testid="delivery-mode-retrait"
              >
                🏪 Retrait
              </button>
            </div>
          </div>

          {/* Delivery Zone - Only if delivery selected */}
          {formData.deliveryMode === 'livraison' && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                Zone de livraison <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {DELIVERY_ZONES.map((zone) => (
                  <button
                    key={zone.id}
                    type="button"
                    onClick={() => handleSelectChange('deliveryZone', zone.id)}
                    className={`h-14 rounded-xl border-2 font-medium text-sm transition-all px-2 ${
                      formData.deliveryZone === zone.id
                        ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                        : 'bg-white border-gray-300 hover:border-burgundy'
                    }`}
                    data-testid={`zone-${zone.id}`}
                  >
                    {zone.label}
                  </button>
                ))}
              </div>
              {formData.deliveryZone === 'above-100km' && (
                <p className="text-amber-600 text-sm flex items-center gap-1 bg-amber-50 p-2 rounded-lg">
                  <AlertCircle size={14} />
                  Les frais de livraison seront confirmés après validation.
                </p>
              )}
            </div>
          )}

          {/* Delivery Date */}
          <div className="space-y-2">
            <Label htmlFor="deliveryDate" className="text-base font-semibold">
              <Calendar className="inline mr-1" size={16} />
              Date de livraison <span className="text-red-500">*</span>
            </Label>
            <Input
              id="deliveryDate"
              name="deliveryDate"
              type="date"
              min={today}
              value={formData.deliveryDate}
              onChange={handleInputChange}
              className="text-base h-12 border-2 border-gray-300 focus:border-burgundy rounded-xl"
              data-testid="delivery-date-input"
              required
            />
          </div>

          {/* Confirmation Method */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Mode de confirmation <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {CONFIRMATION_METHODS.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => handleSelectChange('confirmationMethod', method.id)}
                  className={`h-14 rounded-xl border-2 font-medium text-sm transition-all ${
                    formData.confirmationMethod === method.id
                      ? 'bg-burgundy text-white border-burgundy shadow-lg scale-105'
                      : 'bg-white border-gray-300 hover:border-burgundy'
                  }`}
                  data-testid={`confirm-${method.id}`}
                >
                  {method.icon} {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-base font-semibold">
              Note <span className="text-gray-400">(optionnel)</span>
            </Label>
            <Input
              id="comment"
              name="comment"
              type="text"
              placeholder="Précisions sur votre commande..."
              value={formData.comment}
              onChange={handleInputChange}
              className="text-base h-12 border-2 border-gray-300 focus:border-burgundy rounded-xl"
              data-testid="comment-input"
            />
          </div>

          {/* Price Summary */}
          {formData.product && formData.quantity && parseInt(formData.quantity) >= MIN_QUANTITY && (
            <div className="bg-gold/10 rounded-xl p-4 border border-gold/30">
              <h3 className="font-semibold text-burgundy mb-2">💰 Total</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Sous-total ({formData.quantity} × 3 500 F)</span>
                  <span className="font-medium">{priceCalculation.subtotal.toLocaleString()} F</span>
                </div>
                {formData.deliveryMode === 'livraison' && formData.deliveryZone && (
                  <div className="flex justify-between">
                    <span>Livraison</span>
                    <span className="font-medium">
                      {priceCalculation.deliveryFeeText || `${priceCalculation.deliveryFee.toLocaleString()} F`}
                    </span>
                  </div>
                )}
                <div className="h-px bg-burgundy/20 my-2"></div>
                <div className="flex justify-between text-base font-bold text-burgundy">
                  <span>TOTAL</span>
                  <span>
                    {priceCalculation.isAbove100km 
                      ? `${priceCalculation.subtotal.toLocaleString()} F + livraison`
                      : `${priceCalculation.total.toLocaleString()} F CFA`
                    }
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-2">
            <Button
              type="submit"
              size="lg"
              disabled={!!quantityError}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 shadow-xl hover:shadow-2xl transition-all hover:scale-105 rounded-xl disabled:opacity-50"
              data-testid="preview-btn"
            >
              <MessageCircle className="mr-2" size={24} />
              Envoyer via WhatsApp
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            Vous verrez un récapitulatif avant d'envoyer
          </p>
        </form>

        {/* Contact Info */}
        <div className="mt-8 p-4 bg-white rounded-xl shadow-md border border-gray-200">
          <p className="text-sm font-semibold text-center text-burgundy mb-3">
            📞 Besoin d'aide ?
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="tel:+221781270846" 
              className="flex items-center gap-2 px-4 py-2 bg-burgundy/10 rounded-lg text-burgundy hover:bg-burgundy/20 transition-colors"
            >
              <Phone size={16} />
              78 127 08 46
            </a>
            <a 
              href="tel:+221779093914" 
              className="flex items-center gap-2 px-4 py-2 bg-burgundy/10 rounded-lg text-burgundy hover:bg-burgundy/20 transition-colors"
            >
              <Phone size={16} />
              77 909 39 14
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p className="font-medium">Volaille Prime - DMB Consulting</p>
          <p>Production locale de qualité à Thiès</p>
        </div>
      </div>
    </div>
  );
};

export default QuickOrderPage;
